import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import './OutstandingCallPage.css';

const SitesBeingUpgradedPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [filterText, setFilterText] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch data from UKPN API
  useEffect(() => {
    const fetchUkpnData = async () => {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("Please login again");
          return;
        }
        
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const response = await axios.get(`${baseUrl}/api/ukpn`, config);
        
        if (response.data.success) {
          setData(response.data.data);
        } else {
          toast.error("Failed to fetch data");
        }
      } catch (error) {
        console.error("Error fetching UKPN data:", error);
        toast.error("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchUkpnData();
  }, []);

  // Filter data based on type and search text
  const filteredData = data.filter(item => {
    // First filter by type - show only "Sites Being Upgraded" type (value 2)
    if (!item.type) return false;
    
    const typeValues = item.type.split(',').map(t => t.trim());
    if (!typeValues.includes('2')) return false;
    
    // Then apply search filter
    if (!filterText) return true;
    
    const searchText = filterText.toLowerCase();
    return (
      (item.month && item.month.toLowerCase().includes(searchText)) ||
      (item.priorities && item.priorities.toLowerCase().includes(searchText)) ||
      (item.area && item.area.toLowerCase().includes(searchText)) ||
      (item.siteDetails && item.siteDetails.toLowerCase().includes(searchText)) ||
      (item.postCode && item.postCode.toLowerCase().includes(searchText)) ||
      (item.w3w && item.w3w.toLowerCase().includes(searchText)) ||
      (item.budgetHolder && item.budgetHolder.toLowerCase().includes(searchText)) ||
      (item.dateVisited && item.dateVisited.toLowerCase().includes(searchText)) ||
      (item.maintenanceCost && item.maintenanceCost.toLowerCase().includes(searchText)) ||
      (item.poNumber && item.poNumber.toLowerCase().includes(searchText)) ||
      (item.invoiceNo && item.invoiceNo.toLowerCase().includes(searchText)) ||
      (item.comments2025 && item.comments2025.toLowerCase().includes(searchText)) ||
      (item.additionalInfoComments && item.additionalInfoComments.toLowerCase().includes(searchText))
    );
  });

  // Sort filtered data by updatedAt (latest modified first), then by createdAt
  const sortedData = filteredData.sort((a, b) => {
    // First sort by updatedAt (if available)
    if (a.updatedAt && b.updatedAt) {
      const dateA = new Date(a.updatedAt);
      const dateB = new Date(b.updatedAt);
      if (dateA.getTime() !== dateB.getTime()) {
        return dateB.getTime() - dateA.getTime(); // Descending order (latest first)
      }
    }
    
    // If updatedAt is same or not available, sort by createdAt
    if (a.createdAt && b.createdAt) {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return dateB.getTime() - dateA.getTime(); // Descending order (latest first)
    }
    
    // If no dates available, maintain original order
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = sortedData.slice(startIndex, endIndex);

  return (
    <div className="outstanding-call-page no-max-height">
      <div className="table-container">
        {/* Control Bar */}
        <div className="control-bar">
          <div className="control-right">
            <input
              type="text"
              placeholder="Filter in records..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="filter-input"
            />
          </div>
        </div>

        {/* Data Table */}
        <div className="table-section">
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Month</th>
                <th>Priorities</th>
                <th>Area</th>
                <th>Site Details</th>
                <th>Post Code</th>
                <th>W3W</th>
                <th>Budget Holder</th>
                <th>Date Visited</th>
                <th>Maintenance Cost</th>
                <th>PO Number</th>
                <th>Invoice No</th>
                <th>Comments 2025</th>
                <th>Additional Info/Comments</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="13" style={{ textAlign: 'center', padding: '20px' }}>
                    Loading Sites Being Upgraded data...
                  </td>
                </tr>
              ) : currentData.length === 0 ? (
                <tr>
                  <td colSpan="13" style={{ textAlign: 'center', padding: '20px' }}>
                    No Sites Being Upgraded data found
                  </td>
                </tr>
              ) : (
                currentData.map((item) => (
                  <tr key={item._id || item.id}>
                    <td>{item.month || '-'}</td>
                    <td>{item.priorities || '-'}</td>
                    <td>{item.area || '-'}</td>
                    <td className="site-details-cell">{item.siteDetails || '-'}</td>
                    <td>{item.postCode || '-'}</td>
                    <td>{item.w3w || '-'}</td>
                    <td>{item.budgetHolder || '-'}</td>
                    <td>{item.dateVisited || '-'}</td>
                    <td>{item.maintenanceCost || '-'}</td>
                    <td>{item.poNumber || '-'}</td>
                    <td>{item.invoiceNo || '-'}</td>
                    <td className="additional-notes-cell">{item.comments2025 || '-'}</td>
                    <td className="additional-notes-cell">{item.additionalInfoComments || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
          <button 
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            Previous
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button 
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            Next
          </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SitesBeingUpgradedPage;


